import { useState } from "react";
import { useHistory } from "react-router-dom";
import { DropdownButton, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { globalEnv } from "~/global-env";
import { AccountTypeEnum, type ComplianceObjectType } from "~/graphql/supergraphTypes";
import { ProfileCellRenderParams } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import {
  isIndividualProfile,
  isJointAccountType,
} from "~/pages/Compliance/utils/accountTypeUtils";
import { SPACING } from "~/styles/variables";
import { DeleteProfileModal } from "./DeleteProfileModal";
import { getDescendants } from "./utils";

interface Props {
  params: ProfileCellRenderParams;
  caseId: number;
  accountType?: AccountTypeEnum | null;
  complianceObjectType?: ComplianceObjectType | null;
  readonly: boolean;
  showDelete: boolean;
  allowChildCreation: boolean;
  disabled?: boolean;
}

export const ProfileActions = ({
  params,
  caseId,
  accountType,
  complianceObjectType,
  readonly,
  showDelete,
  allowChildCreation,
  disabled = false,
}: Props) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const guidedProfileCreation = useGuidedProfileCreation();
  const profileId = params.value.id;

  const viewAction = () => {
    const queryParam = `?profileId=${profileId}`;
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/profile-details${queryParam}`);
  };

  const newProfileAction = () => {
    const queryParam = `?parentProfileId=${profileId}`;
    if (guidedProfileCreation) {
      history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles/create${queryParam}`);
    } else {
      history.push(
        `${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers${queryParam}`,
      );
    }
  };

  const newJointChildOwnerAction = () => {
    const queryParam = `?parentProfileId=${profileId}`;
    history.push(
      `${globalEnv.complianceRoute}/case/${caseId}/create-individual-owner${queryParam}`,
    );
  };

  const editAction = () => {
    const queryParam = `?profileId=${profileId}`;
    history.push(`${globalEnv.complianceRoute}/case/${caseId}/edit-profile${queryParam}`);
  };

  const deleteAction = () => {
    setIsOpen(true);
  };

  const descendants = getDescendants(params.node.childrenAfterGroup);
  const isOrHasPaymentBeneficiary =
    params.value.isPaymentBeneficiary ||
    descendants.some((descendant) => descendant.isPaymentBeneficiary);

  const renderChildCreationAction = () => {
    if (isIndividualProfile(accountType, complianceObjectType)) {
      return (
        <DropdownButton.Action disabled onSelect={() => undefined}>
          <ExtendedInfoTooltip message="Individuals cannot have child owners or controllers">
            <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
              <Icon type="plus" />
              <Text>New child owner or controller</Text>
            </Flex>
          </ExtendedInfoTooltip>
        </DropdownButton.Action>
      );
    }

    if (isJointAccountType(accountType)) {
      return (
        <DropdownButton.Action disabled={readonly} onSelect={newJointChildOwnerAction}>
          <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
            <Icon type="plus" />
            <Text>New child owner</Text>
          </Flex>
        </DropdownButton.Action>
      );
    }

    return (
      <DropdownButton.Action disabled={readonly} onSelect={newProfileAction}>
        <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
          <Icon type="plus" />
          <Text>New child owner or controller</Text>
        </Flex>
      </DropdownButton.Action>
    );
  };

  return (
    <>
      <DeleteProfileModal
        profileId={params.value.id}
        isOrHasPaymentBeneficiary={isOrHasPaymentBeneficiary}
        profileName={params.value.name}
        caseId={caseId}
        isOpen={isOpen}
        descendants={descendants}
      />
      <DropdownButton
        data-testid={`profile-${profileId}-actions`}
        css={{ margin: SPACING.X_SMALL }}
        variant="muted"
        icon="ellipsis-h"
        direction="down"
        align="right"
        usePortal
        disabled={disabled}
      >
        <DropdownButton.Action onSelect={viewAction}>
          <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
            <Icon type="arrow-right" />
            <Text>View</Text>
          </Flex>
        </DropdownButton.Action>
        {allowChildCreation &&
          (guidedProfileCreation ? (
            renderChildCreationAction()
          ) : (
            <DropdownButton.Action disabled={readonly} onSelect={newProfileAction}>
              <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
                <Icon type="plus" />
                <Text>New child owner or controller</Text>
              </Flex>
            </DropdownButton.Action>
          ))}
        <DropdownButton.Action disabled={readonly} onSelect={editAction}>
          <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
            <Icon type="edit" />
            <Text>Edit</Text>
          </Flex>
        </DropdownButton.Action>
        {showDelete && (
          <DropdownButton.Action disabled={readonly} onSelect={deleteAction}>
            <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
              <Icon type="trash" />
              <Text>Delete</Text>
            </Flex>
          </DropdownButton.Action>
        )}
      </DropdownButton>
    </>
  );
};
